/**
 * App entry resolver + interface-declaration LAUNCHER (ADR-0049 Slice C2).
 *
 * `/apps/:appId` resolves the app's HOME interface and bounces into the builder.
 * The owner's model: an app is a backend wearing INTERFACES (faces), and the user
 * DECLARES which to create — it is NOT auto-given a web page. So a brand-new app
 * (no pages, no chat/voce, no forms) shows a launcher of cards (Web app · Chat ·
 * Voce · Form), each "+ Crea"; picking one creates that interface and opens its
 * editor. Once ≥1 interface exists, we resolve the home and redirect:
 *   web (has pages) → the home page builder; else chat → voce → form (page-less
 *   builder, ADR-0049 Slice C1). "Disabling web" is gone — web present ⟺ has pages.
 *
 * Adding MORE interfaces later happens from the builder's interface selector.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, FileText, Globe, MessageSquare, Mic, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { PageContainer, PageContent, PageHeader } from "@/components/ui/page";
import { appsApi } from "@/lib/api-apps";
import { usePermissions } from "@/lib/permissions";

export function AppEntryPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { appId = "" } = useParams<{ appId: string }>();
  const { can } = usePermissions();
  const canWrite = can("apps", "write");

  const appQ = useQuery({
    queryKey: ["apps", appId],
    queryFn: () => appsApi.get(appId),
    enabled: !!appId,
  });
  const pagesQ = useQuery({
    queryKey: ["apps", appId, "pages"],
    queryFn: () => appsApi.listPages(appId),
    enabled: !!appId,
  });
  const interfacesQ = useQuery({
    queryKey: ["apps", appId, "interfaces"],
    queryFn: () => appsApi.listInterfaces(appId),
    enabled: !!appId,
  });
  const formsQ = useQuery({
    queryKey: ["apps", appId, "forms"],  // canonical — shares cache with AppFormsPane (audit 2026-07-02)
    queryFn: () => appsApi.listForms(appId),
    enabled: !!appId,
  });

  // Create the web app's first page → the page builder.
  const createWebApp = useMutation({
    mutationFn: () => appsApi.createPage(appId, { title: "Home", slug: "home", is_home: true }),
    onSuccess: (page) => {
      qc.invalidateQueries({ queryKey: ["apps", appId, "pages"] });
      navigate(`/apps/${appId}/pages/${page.id}`, { replace: true });
    },
  });
  // Create a chat / voce assistant interface → the page-less builder on it.
  const createAssistant = useMutation({
    mutationFn: (kind: "chat" | "voice") =>
      appsApi.createInterface(appId, { kind, name: kind === "voice" ? "Assistente" : "Chat" }),
    onSuccess: (iface) => {
      qc.invalidateQueries({ queryKey: ["apps", appId, "interfaces"] });
      navigate(`/apps/${appId}/builder?iface=${iface.kind}`, { replace: true });
    },
  });

  if (appQ.isError) {
    return (
      <PageContainer>
        <PageContent>
          <Alert tone="danger" title={t("apps.loadAppFailed", "Could not load app")}>
            <span className="font-mono text-xs">{(appQ.error as Error).message}</span>
          </Alert>
        </PageContent>
      </PageContainer>
    );
  }

  // Wait for the whole picture before resolving the home (else we'd flash the
  // launcher for an app that actually has interfaces).
  if (
    pagesQ.isLoading ||
    !pagesQ.data ||
    interfacesQ.isLoading ||
    !interfacesQ.data ||
    formsQ.isLoading ||
    !formsQ.data
  ) {
    return (
      <PageContainer>
        <PageContent>
          <p className="text-sm text-muted-foreground">{t("common.loading", "Loading…")}</p>
        </PageContent>
      </PageContainer>
    );
  }

  // Home resolution (ADR-0049): web (has pages) → chat → voce → form. The first
  // available front door wins; adding others happens from the builder selector.
  const entry = pagesQ.data.find((p) => p.is_home) ?? pagesQ.data[0];
  if (entry) return <Navigate to={`/apps/${appId}/pages/${entry.id}`} replace />;
  const chat = interfacesQ.data.find((i) => i.kind === "chat");
  const voice = interfacesQ.data.find((i) => i.kind === "voice");
  if (chat) return <Navigate to={`/apps/${appId}/builder?iface=chat`} replace />;
  if (voice) return <Navigate to={`/apps/${appId}/builder?iface=voice`} replace />;
  if (formsQ.data.length > 0) return <Navigate to={`/apps/${appId}/builder?iface=form`} replace />;

  // Nothing declared yet — the interface launcher.
  const busy = createWebApp.isPending || createAssistant.isPending;
  const error = createWebApp.error || createAssistant.error;
  const cards = [
    {
      key: "web",
      icon: Globe,
      title: t("apps.ifaceWebApp", "Web app"),
      desc: t("apps.ifaceWebAppDesc", "Pagine, layout e logica del portale."),
      onCreate: () => createWebApp.mutate(),
    },
    {
      key: "chat",
      icon: MessageSquare,
      title: t("apps.ifaceChat", "Chat"),
      desc: t("apps.ifaceChatDesc", "Assistente in chat (stile ChatGPT) sull'app."),
      onCreate: () => createAssistant.mutate("chat"),
    },
    {
      key: "voice",
      icon: Mic,
      title: t("apps.ifaceVoice", "Voce"),
      desc: t("apps.ifaceVoiceDesc", "Assistente vocale (stile Jarvis) sull'app."),
      onCreate: () => createAssistant.mutate("voice"),
    },
    {
      key: "form",
      icon: FileText,
      title: t("apps.ifaceForm", "Form"),
      desc: t("apps.ifaceFormDesc", "Form pubblici di cattura-dati sulle tabelle."),
      onCreate: () => navigate(`/apps/${appId}/builder?iface=form`),
    },
  ];

  return (
    <PageContainer>
      <PageHeader
        title={appQ.data?.name ?? t("apps.loading", "Loading…")}
        description={t(
          "apps.launcherSubtitle",
          "Scegli quali interfacce creare per questa app. Potrai aggiungerne altre in qualsiasi momento.",
        )}
        actions={
          <Button variant="ghost" size="sm" onClick={() => navigate("/apps")}>
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
            {t("apps.backToApps", "Torna alle app")}
          </Button>
        }
      />
      <PageContent>
        <div className="grid gap-3 sm:grid-cols-2">
          {cards.map((c) => (
            <div
              key={c.key}
              className="flex flex-col rounded-lg border border-border bg-surface p-4"
            >
              <div className="flex items-center gap-2.5">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent/10 text-accent">
                  <c.icon className="h-4.5 w-4.5" strokeWidth={1.75} />
                </span>
                <h3 className="text-sm font-semibold text-foreground">{c.title}</h3>
              </div>
              <p className="mt-2 flex-1 text-sm text-muted-foreground">{c.desc}</p>
              {canWrite && (
                <Button
                  className="mt-3 self-start"
                  size="sm"
                  variant="outline"
                  disabled={busy}
                  onClick={c.onCreate}
                >
                  <Plus className="h-4 w-4" strokeWidth={1.5} /> {t("common.create", "Crea")}
                </Button>
              )}
            </div>
          ))}
        </div>
        {error && (
          <Alert
            tone="danger"
            title={t("apps.createInterfaceFailed", "Impossibile creare l'interfaccia")}
            className="mt-3"
          >
            <span className="font-mono text-xs">{(error as Error).message}</span>
          </Alert>
        )}
      </PageContent>
    </PageContainer>
  );
}
